import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Table, Select, Dropdown, Modal } from 'antd';
import type { TableColumnsType, ThemeConfig, MenuProps } from 'antd';
import { fontFamily } from '../../theme/tokens';
import Pagination from '../../components/Pagination';
import { UploadIcon, PlusIcon, KebabIcon, EditSquareIcon, TrashIcon } from '../../assets/icons';
import sarah from '../../assets/avatars/sarah-jenkins.png';
import david from '../../assets/avatars/david-chen.png';
import elena from '../../assets/avatars/elena-rodriguez.png';
import admin from '../../assets/avatars/admin-user.png';

type UserStatus = 'active' | 'inactive';

interface User {
  id: string;
  name: string;
  empId: string;
  email: string;
  phone: string;
  roles: string[];
  hierarchy: string;
  status: UserStatus;
  lastSeen: string;
  avatar: string;
}

/** Sample dataset mirroring the Figma "User Management" list. */
const SAMPLE_USERS: User[] = [
  { id: '1', name: 'Priyanka Sharma', empId: 'ADM-2024001', email: 'p.sharma@chunaav.gov.in', phone: '+91 98765-43210', roles: ['State Admin', 'Observer'], hierarchy: 'Alwar', status: 'active', lastSeen: 'Last: 12m ago', avatar: sarah },
  { id: '2', name: 'Arjun Reddy', empId: 'ADM-2024089', email: 'a.reddy@chunaav.gov.in', phone: '+91 77654-32109', roles: ['IT Auditor'], hierarchy: 'Alwar', status: 'inactive', lastSeen: 'Last: 4d ago', avatar: david },
  { id: '3', name: 'Meera Nair', empId: 'ADM-2024102', email: 'm.nair@chunaav.gov.in', phone: '+91 90123-45678', roles: ['State Admin'], hierarchy: 'Jaipur', status: 'active', lastSeen: 'Last: 1h ago', avatar: elena },
  { id: '4', name: 'Rahul Verma', empId: 'ADM-2024117', email: 'r.verma@chunaav.gov.in', phone: '+91 88990-11223', roles: ['Observer'], hierarchy: 'Ajmer', status: 'active', lastSeen: 'Last: 3h ago', avatar: admin },
  { id: '5', name: 'Sana Khan', empId: 'ADM-2024131', email: 's.khan@chunaav.gov.in', phone: '+91 70654-99881', roles: ['IT Auditor', 'Observer'], hierarchy: 'Mumbai', status: 'inactive', lastSeen: 'Last: 2w ago', avatar: sarah },
  { id: '6', name: 'Vikram Singh', empId: 'ADM-2024145', email: 'v.singh@chunaav.gov.in', phone: '+91 99887-66554', roles: ['State Admin'], hierarchy: 'Pune', status: 'active', lastSeen: 'Last: 22m ago', avatar: david },
  { id: '7', name: 'Anita Desai', empId: 'ADM-2024150', email: 'a.desai@chunaav.gov.in', phone: '+91 91234-00987', roles: ['Observer'], hierarchy: 'Alwar', status: 'active', lastSeen: 'Last: 5h ago', avatar: elena },
  { id: '8', name: 'Karan Mehta', empId: 'ADM-2024168', email: 'k.mehta@chunaav.gov.in', phone: '+91 80012-34567', roles: ['IT Auditor'], hierarchy: 'Jaipur', status: 'inactive', lastSeen: 'Last: 6d ago', avatar: admin },
  { id: '9', name: 'Divya Rao', empId: 'ADM-2024172', email: 'd.rao@chunaav.gov.in', phone: '+91 90090-80080', roles: ['State Admin', 'Observer'], hierarchy: 'Nagpur', status: 'active', lastSeen: 'Last: 40m ago', avatar: sarah },
  { id: '10', name: 'Imran Ali', empId: 'ADM-2024190', email: 'i.ali@chunaav.gov.in', phone: '+91 78901-23456', roles: ['Observer'], hierarchy: 'Ajmer', status: 'active', lastSeen: 'Last: 8h ago', avatar: david },
  { id: '11', name: 'Neha Gupta', empId: 'ADM-2024201', email: 'n.gupta@chunaav.gov.in', phone: '+91 96543-21098', roles: ['IT Auditor'], hierarchy: 'Mumbai', status: 'inactive', lastSeen: 'Last: 3w ago', avatar: elena },
  { id: '12', name: 'Sanjay Patel', empId: 'ADM-2024216', email: 's.patel@chunaav.gov.in', phone: '+91 93210-45600', roles: ['State Admin'], hierarchy: 'Pune', status: 'active', lastSeen: 'Last: 15m ago', avatar: admin },
];

const ROLES = ['State Admin', 'Observer', 'IT Auditor'];

/** Per-role pill colours from Figma. */
const ROLE_PILL: Record<string, string> = {
  'State Admin': 'bg-[#dbeafe] border-[#bfdbfe] text-brand',
  Observer: 'bg-[#f1f5f9] border-[#e2e8f0] text-[#475569]',
  'IT Auditor': 'bg-[#fff7ed] border-[#fed7aa] text-[#c2410c]',
};

const TABLE_THEME: ThemeConfig = {
  token: { colorBorderSecondary: '#e2e8f0', fontFamily },
  components: {
    Table: {
      headerBg: 'rgba(248,250,252,0.5)',
      headerColor: '#64748b',
      headerSplitColor: 'transparent',
      borderColor: '#e2e8f0',
      cellPaddingBlock: 20,
      cellPaddingInline: 24,
      rowHoverBg: '#f8fafc',
      colorText: '#334155',
      fontSize: 14,
    },
  },
};

function RolePill({ role }: { role: string }) {
  return (
    <span
      className={[
        'inline-flex items-center rounded-full border px-[11px] py-[3px] text-[10px] font-semibold uppercase tracking-[0.5px]',
        ROLE_PILL[role] ?? 'bg-[#f1f5f9] border-[#e2e8f0] text-[#475569]',
      ].join(' ')}
    >
      {role}
    </span>
  );
}

function StatusCell({ status, lastSeen }: { status: UserStatus; lastSeen: string }) {
  const active = status === 'active';
  return (
    <div className="flex flex-col items-center gap-1">
      <span
        className={[
          'inline-flex items-center gap-1.5 rounded-full border px-[11px] py-[5px] text-xs font-medium capitalize',
          active ? 'border-[#bbf7d0] bg-[#f0fdf4] text-[#15803d]' : 'border-[#e2e8f0] bg-[#f1f5f9] text-[#475569]',
        ].join(' ')}
      >
        <span className={['size-1.5 rounded-full', active ? 'bg-[#22c55e]' : 'bg-[#94a3b8]'].join(' ')} />
        {status}
      </span>
      <span className="text-[10px] text-[#94a3b8]">{lastSeen}</span>
    </div>
  );
}

const columns: TableColumnsType<User> = [
  {
    title: 'User Info',
    key: 'user',
    width: 240,
    render: (_, u) => (
      <div className="flex items-center gap-4">
        <img src={u.avatar} alt={u.name} className="size-10 shrink-0 rounded-full object-cover" />
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-[#0f172a]">{u.name}</span>
          <span className="text-[11px] text-[#64748b]">ID: {u.empId}</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Contact',
    key: 'contact',
    width: 240,
    render: (_, u) => (
      <div className="flex flex-col gap-0.5">
        <span className="text-sm text-[#334155]">{u.email}</span>
        <span className="text-xs text-[#64748b]">{u.phone}</span>
      </div>
    ),
  },
  {
    title: 'Functional Roles',
    key: 'roles',
    width: 180,
    render: (_, u) => (
      <div className="flex flex-col items-start gap-1.5">
        {u.roles.map((r) => (
          <RolePill key={r} role={r} />
        ))}
      </div>
    ),
  },
  {
    title: 'Organization Hierarchy',
    key: 'org',
    width: 150,
    render: (_, u) => <span className="text-sm text-[#334155]">{u.hierarchy}</span>,
  },
  {
    title: 'Status',
    key: 'status',
    width: 150,
    align: 'center',
    render: (_, u) => <StatusCell status={u.status} lastSeen={u.lastSeen} />,
  },
];

function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const items: MenuProps['items'] = [
    { key: 'edit', label: 'Edit', icon: <EditSquareIcon className="size-4" /> },
    { type: 'divider' },
    { key: 'delete', label: 'Delete', danger: true, icon: <TrashIcon className="size-4" /> },
  ];
  const onClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'edit') onEdit();
    else if (key === 'delete') onDelete();
  };
  return (
    <Dropdown menu={{ items, onClick }} trigger={['click']} placement="bottomRight">
      <button
        type="button"
        aria-label="Row actions"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex size-8 items-center justify-center rounded-lg text-[#94a3b8] transition-colors hover:bg-brand-shade hover:text-brand"
      >
        <KebabIcon className="h-[15px] w-[3px] rotate-90" />
      </button>
    </Dropdown>
  );
}

const LABEL = 'text-[11px] font-semibold uppercase tracking-[0.55px] text-[#64748b]';

export default function UsersPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [level, setLevel] = useState('');
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  const levels = useMemo(() => Array.from(new Set(SAMPLE_USERS.map((u) => u.hierarchy))).sort(), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SAMPLE_USERS.filter((u) => {
      if (deletedIds.has(u.id)) return false;
      if (q && !(u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.empId.toLowerCase().includes(q)))
        return false;
      if (role && !u.roles.includes(role)) return false;
      if (level && u.hierarchy !== level) return false;
      if (status && u.status !== status) return false;
      return true;
    });
  }, [query, role, level, status, deletedIds]);

  const handleDelete = (user: User) => {
    Modal.confirm({
      title: 'Delete user',
      content: `Are you sure you want to delete ${user.name}? This action cannot be undone.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => setDeletedIds((prev) => new Set(prev).add(user.id)),
    });
  };

  const columnsWithActions: TableColumnsType<User> = [
    ...columns,
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      align: 'right',
      render: (_, u) => (
        <RowActions onEdit={() => navigate('/admin/users/new')} onDelete={() => handleDelete(u)} />
      ),
    },
  ];

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);

  const clearFilters = () => {
    setQuery('');
    setRole('');
    setLevel('');
    setStatus('');
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-9 text-ink">User Management</h1>
          <p className="text-sm text-ink-secondary">
            Manage administrative personnel, roles, and organizational hierarchies.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className="flex items-center gap-2 rounded-md border border-[#cbd5e1] bg-white px-[17px] py-[9px] text-sm font-medium text-ink transition-colors hover:bg-brand-shade"
          >
            <UploadIcon className="h-[14px] w-[12px]" />
            Bulk Upload
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/users/new')}
            className="flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            <PlusIcon className="size-3.5" />
            Create User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-line-soft bg-white px-5 pb-5 pt-6 shadow-card">
        <div className="flex flex-wrap items-end gap-6">
          <div className="flex min-w-[160px] flex-1 flex-col gap-2">
            <span className={LABEL}>Name / Email</span>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="By Identity..."
              className="w-full rounded-md border border-[#cbd5e1] bg-white px-[13px] py-2.5 text-sm text-ink shadow-card placeholder:text-[#6b7280] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>

          <div className="flex w-full flex-col gap-2 sm:w-[192px]">
            <span className={LABEL}>Role</span>
            <Select
              className="w-full"
              value={role}
              onChange={(v) => {
                setRole(v);
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Roles' },
                ...ROLES.map((r) => ({ value: r, label: r })),
              ]}
            />
          </div>

          <div className="flex w-full flex-col gap-2 sm:w-[192px]">
            <span className={LABEL}>Org Level</span>
            <Select
              className="w-full"
              value={level}
              onChange={(v) => {
                setLevel(v);
                setPage(1);
              }}
              options={[
                { value: '', label: 'All Levels' },
                ...levels.map((l) => ({ value: l, label: l })),
              ]}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className={LABEL}>Status</span>
            <div className="flex items-center gap-1 rounded-lg bg-[#f1f5f9] p-1">
              {(['active', 'inactive'] as const).map((s) => {
                const selected = status === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setStatus(selected ? '' : s);
                      setPage(1);
                    }}
                    className={[
                      'rounded-md px-6 py-1.5 text-sm font-medium capitalize transition-colors',
                      selected ? 'bg-brand text-white shadow-btn' : 'text-[#475569] hover:text-brand',
                    ].join(' ')}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={clearFilters}
            className="h-[38px] rounded-md bg-[#e2e8f0] px-6 text-sm font-semibold text-[#334155] transition-colors hover:brightness-95"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-line-soft bg-white shadow-card">
        <ConfigProvider theme={TABLE_THEME}>
          <Table<User>
            className="chunaav-table"
            columns={columnsWithActions}
            dataSource={pageRows}
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </ConfigProvider>

        {/* Footer / pagination */}
        <Pagination
          rangeStart={total === 0 ? 0 : start + 1}
          rangeEnd={Math.min(start + pageSize, total)}
          total={total}
          noun="users"
          page={current}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
